import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Table, Menu, MenuItem } from "flux";
import useAdAssetGroups from "../../hooks/use-ad-asset-groups";
import CreateAdAssetGroupDialog from "../create-ad-asset-group-dialog";
import useMutateAdAssetGroup from "../../hooks/use-mutate-ad-asset-group";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { AdAssetGroup } from "../../types/ad-asset-group";
import { publishMultiPlatform, updateAdAssetGroup } from "../../features/ad-publishing/api";
import { getLocaleTimeString } from "@adora/shared/utils/date";
import ConfirmationDialog from "@adora/shared/components/confirm-dialog";
import ErrorDialog from "../error-dialog";
import Page from "@adora/shared/components/layout/page";
import useBrandName from "../../hooks/use-brand-name";
import { AdAssetGroupStatusEnum } from "@adora/shared/types/ad-asset-group";

const AdAssetGroupsPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const brandName = useBrandName(brandId);
  const navigate = useNavigate();
  const {
    adAssetGroups,
    isLoading: groupsLoading,
    refetchAdAssetGroups
  } = useAdAssetGroups(brandId ? Number(brandId) : null);
  const { openDialog } = useDialog();
  const {
    deleteAdAssetGroup,
    duplicateAdAssetGroup,
    archiveAdAssetGroup,
    unarchiveAdAssetGroup,
    isLoading: isDeleting
  } = useMutateAdAssetGroup();

  const handleCreateAdAssetGroup = async () => {
    if (!brandId) return;
    const result = await openDialog(CreateAdAssetGroupDialog, {
      brandId: Number(brandId)
    });
    if (result) refetchAdAssetGroups();
  };

  const handleDuplicateAdAssetGroup = async (adAssetGroupId: number) => {
    if (!brandId) return;
    try {
      const result = await duplicateAdAssetGroup(Number(brandId), adAssetGroupId);
      result.match(
        (error) => {
          openDialog(ErrorDialog, {
            title: "Error Duplicating Ad Asset Group",
            error
          });
        },
        (response) => {
          if (response) {
            refetchAdAssetGroups();
          }
        }
      );
    } catch (error) {
      console.error("Error duplicating ad asset group:", error);
    }
  };

  const handleArchiveAdAssetGroup = async (adAssetGroupId: number) => {
    if (!brandId) return;
    try {
      const result = await archiveAdAssetGroup(Number(brandId), adAssetGroupId);
      result.match(
        (error) => {
          openDialog(ErrorDialog, {
            title: "Error Archiving Ad Asset Group",
            error
          });
        },
        (response) => {
          if (response) {
            refetchAdAssetGroups();
          }
        }
      );
    } catch (error) {
      console.error("Error archiving ad asset group:", error);
    }
  };

  const handleUnarchiveAdAssetGroup = async (adAssetGroupId: number) => {
    if (!brandId) return;
    try {
      const result = await unarchiveAdAssetGroup(Number(brandId), adAssetGroupId);
      result.match(
        (error) => {
          openDialog(ErrorDialog, {
            title: "Error Unarchiving Ad Asset Group",
            error
          });
        },
        (response) => {
          if (response) {
            refetchAdAssetGroups();
          }
        }
      );
    } catch (error) {
      console.error("Error unarchiving ad asset group:", error);
    }
  };

  const handlePublish = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const confirmed = window.confirm(
      "Are you sure you want to publish this Ad Asset Group? This will stage the ad asset group in the external ad platform."
    );
    if (confirmed) {
      try {
        const result = await publishMultiPlatform({
          brandId: Number(brandId),
          adAssetGroupId
        });

        result.match(
          (error) => {
            openDialog(ErrorDialog, {
              title: "Error",
              error
            });
          },
          () => {
            refetchAdAssetGroups();
          }
        );
      } catch (error) {
        console.error("Error publishing ad asset group:", error);
      }
    }
  };

  const handleDelete = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this Ad Asset Group? This action cannot be undone."
    );
    if (confirmed) {
      const success = await deleteAdAssetGroup(adAssetGroupId, Number(brandId));
      if (success) {
        refetchAdAssetGroups();
      }
    }
  };

  const handleSendForReview = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const result = await updateAdAssetGroup(adAssetGroupId, Number(brandId), {
      status: AdAssetGroupStatusEnum.READY_FOR_REVIEW,
      locked: false
    });
    result.match(
      (error) => {
        openDialog(ConfirmationDialog, {
          title: "Error",
          description: error.message,
          showCancel: false,
          primaryButtonText: "Ok"
        });
      },
      () => {
        refetchAdAssetGroups();
      }
    );
  };

  const handleSendForPublish = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const confirmed = window.confirm(
      "Are you sure you want to send this Ad Asset Group for review? External users will then be able to view and publish."
    );
    if (confirmed) {
      const result = await updateAdAssetGroup(adAssetGroupId, Number(brandId), {
        status: AdAssetGroupStatusEnum.READY_TO_PUBLISH,
        locked: false
      });
      result.match(
        (error) => {
          openDialog(ConfirmationDialog, {
            title: "Error",
            description: error.message,
            showCancel: false,
            primaryButtonText: "Ok"
          });
        },
        () => {
          refetchAdAssetGroups();
        }
      );
    }
  };

  const handleMarkRevising = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const result = await updateAdAssetGroup(adAssetGroupId, Number(brandId), {
      status: AdAssetGroupStatusEnum.REVISING
    });
    result.match(
      (error) => {
        openDialog(ConfirmationDialog, {
          title: "Error",
          description: error.message,
          showCancel: false,
          primaryButtonText: "Ok"
        });
      },
      () => {
        refetchAdAssetGroups();
      }
    );
  };

  const handleSetToDraft = async (adAssetGroupId: number) => {
    if (!brandId) return;
    const result = await updateAdAssetGroup(adAssetGroupId, Number(brandId), {
      status: AdAssetGroupStatusEnum.DRAFT,
      locked: false
    });
    result.match(
      (error) => {
        openDialog(ConfirmationDialog, {
          title: "Error",
          description: error.message,
          showCancel: false,
          primaryButtonText: "Ok"
        });
      },
      () => {
        refetchAdAssetGroups();
      }
    );
  };

  const handleViewAdAssets = (adAssetGroupId: number) => {
    navigate(`/brands/${brandId}/ad-asset-groups/${adAssetGroupId}/ad_assets`);
  };

  const ActionMenu: React.FC<{ adAssetGroup: AdAssetGroup }> = ({ adAssetGroup }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const menuItems = [
      <MenuItem
        key="view"
        onClick={() => {
          handleViewAdAssets(adAssetGroup.id);
          handleMenuClose();
        }}
      >
        View / Edit
      </MenuItem>,
      <MenuItem
        key="duplicate"
        onClick={() => {
          handleDuplicateAdAssetGroup(adAssetGroup.id);
          handleMenuClose();
        }}
      >
        Duplicate
      </MenuItem>,
      adAssetGroup.status == "archived" ? (
        <MenuItem
          key="unarchive"
          onClick={() => {
            handleUnarchiveAdAssetGroup(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Unarchive
        </MenuItem>
      ) : (
        <MenuItem
          key="archive"
          onClick={() => {
            handleArchiveAdAssetGroup(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Archive
        </MenuItem>
      )
    ];

    if (
      [
        AdAssetGroupStatusEnum.DRAFT,
        AdAssetGroupStatusEnum.REVISING,
        AdAssetGroupStatusEnum.ASSEMBLING,
        AdAssetGroupStatusEnum.READY_FOR_REVIEW,
        AdAssetGroupStatusEnum.READY_TO_PUBLISH
      ].includes(adAssetGroup.status as AdAssetGroupStatusEnum)
    ) {
      menuItems.push(
        <MenuItem
          key="send_for_review"
          onClick={() => {
            handleSendForReview(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Send For Review (Creative)
        </MenuItem>,
        <MenuItem
          key="send_for_publish"
          onClick={() => {
            handleSendForPublish(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Send For Publish (Perf)
        </MenuItem>,
        <MenuItem
          key="delete"
          onClick={() => {
            handleDelete(adAssetGroup.id);
            handleMenuClose();
          }}
          style={{ color: "red" }}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </MenuItem>
      );
    }

    if (
      [
        AdAssetGroupStatusEnum.ASSEMBLING,
        AdAssetGroupStatusEnum.DRAFT,
        AdAssetGroupStatusEnum.READY_FOR_REVIEW,
        AdAssetGroupStatusEnum.READY_TO_PUBLISH,
        AdAssetGroupStatusEnum.REVISING
      ].includes(adAssetGroup.status as AdAssetGroupStatusEnum)
    ) {
      menuItems.push(
        <MenuItem
          key="publish"
          onClick={() => {
            handlePublish(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Publish
        </MenuItem>
      );
    }

    if ([AdAssetGroupStatusEnum.FAILED].includes(adAssetGroup.status as AdAssetGroupStatusEnum)) {
      menuItems.push(
        <MenuItem
          key="revise"
          onClick={() => {
            handleMarkRevising(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Revise
        </MenuItem>
      );
    }

    // Add "Set to Draft" option for all statuses except draft
    if (adAssetGroup.status !== AdAssetGroupStatusEnum.DRAFT) {
      menuItems.push(
        <MenuItem
          key="set_to_draft"
          onClick={() => {
            handleSetToDraft(adAssetGroup.id);
            handleMenuClose();
          }}
        >
          Set to Draft
        </MenuItem>
      );
    }

    return (
      <>
        <Button
          onClick={handleMenuOpen}
          variant="text"
          color="inherit"
          size="small"
          dataDogIdentifier="ad asset groups page ellipsis action"
        >
          ...
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {menuItems}
        </Menu>
      </>
    );
  };

  const columns = [
    { field: "id", value: "ID", width: 100 },
    { field: "name", value: "Name", width: 500 },
    { field: "description", value: "Description", width: 200 },
    {
      field: "created_at",
      value: "Created At",
      width: 200,
      renderCell: (row: AdAssetGroup) => getLocaleTimeString(row.created_at)
    },
    {
      field: "updated_at",
      value: "Updated At",
      width: 300,
      renderCell: (row: AdAssetGroup) => getLocaleTimeString(row.updated_at)
    },
    {
      field: "status",
      value: "Status",
      width: 200,
      renderCell: (row: AdAssetGroup) =>
        row.locked &&
        [AdAssetGroupStatusEnum.READY_FOR_REVIEW, AdAssetGroupStatusEnum.READY_TO_PUBLISH].includes(
          row.status as AdAssetGroupStatusEnum
        )
          ? `${row.status} (revising)`
          : row.status
    },
    {
      field: "actions",
      value: "Actions",
      width: 100,
      renderCell: (row: AdAssetGroup) => <ActionMenu adAssetGroup={row} />
    }
  ];

  return (
    <Page
      title="Ad Asset Groups"
      backLinkText={brandName}
      titleBarActions={
        <Button
          variant="contained"
          onClick={handleCreateAdAssetGroup}
          disabled={!brandId}
          dataDogIdentifier="ad asset groups page create ad asset group"
        >
          Create Ad Asset Group
        </Button>
      }
    >
      <Table
        columns={columns}
        rows={adAssetGroups}
        isLoading={groupsLoading}
        stickyHeaders={true}
        height={"85vh"}
        dataDogIdentifier="ad asset groups page"
      />
    </Page>
  );
};

export default AdAssetGroupsPage;
